// BKG performance harness - not a product test. See __perf__/README.md.
// Measures how much of the BKG filter tree is instantiated while collapsed,
// and what one filter interaction costs in getFilterDoc().

import React from "react";
import { render } from "@testing-library/react";
import { FilterUI, FilterInterface } from "../SearchFilters";
import { buildFilterDoc, type FilterValues } from "../filterState";

jest.setTimeout(900000);

const makeSchema = (
  groups: number,
  leavesPerGroup: number,
): FilterInterface => ({
  path: "value",
  children: Array.from({ length: groups }, (_, g) => ({
    path: `value.group_${g}`,
    children: Array.from({ length: leavesPerGroup }, (_, l) => {
      const kind = l % 3;
      if (kind === 0) {
        return {
          path: `value.group_${g}.enum_${l}.value`,
          type: "string",
          allowed_values: Array.from(
            { length: 40 },
            (_, v) => `Value ${g}-${l}-${v}`,
          ),
        };
      }
      if (kind === 1) {
        return {
          path: `value.group_${g}.range_${l}.value`,
          type: "number",
          min: 0,
          max: 10000,
        };
      }
      return {
        path: `value.group_${g}.date_${l}.value`,
        type: "date",
        min: "2020-01-01",
        max: "2026-01-01",
      };
    }),
  })),
});

const report: string[] = [];
const log = (line: string) => report.push(line);

afterAll(() => {
  // eslint-disable-next-line no-console
  console.log(
    "\n\n===== STEP 0: BKG FILTER TREE =====\n" + report.join("\n") + "\n",
  );
});

const measureCollapsed = (
  label: string,
  filterData: FilterInterface,
  primaryFacets?: readonly string[],
  search?: string,
) => {
  const t0 = Date.now();
  const { container, unmount } = render(
    <FilterUI
      filterData={filterData}
      value={{}}
      onChange={() => {}}
      primaryFacets={primaryFacets}
      search={search}
    />,
  );
  const mountMs = Date.now() - t0;

  const summaries = container.querySelectorAll(
    '[class*="AccordionSummary-button"]',
  ).length;
  const expanded = container.querySelectorAll(
    '[class*="AccordionSummary-button"][aria-expanded="true"]',
  ).length;
  const hiddenRegions = container.querySelectorAll(
    '[role="region"][hidden]',
  ).length;
  const inputs = container.querySelectorAll("input").length;
  const nodes = container.querySelectorAll("*").length;

  log(
    `${label} | ` +
      `accordions=${summaries} (expanded=${expanded}, hidden regions=${hiddenRegions}) | ` +
      `<input> mounted=${inputs} | DOM nodes=${nodes} | jsdom mount=${mountMs}ms`,
  );
  unmount();
};

describe("filter tree structural cost", () => {
  it("instantiates the whole tree even with every accordion collapsed", () => {
    for (const [groups, leaves] of [
      [6, 5],
      [12, 10],
    ] as Array<[number, number]>) {
      measureCollapsed(
        `schema ${groups}x${leaves} = ${groups * leaves} leaves`,
        makeSchema(groups, leaves),
      );
    }
  });

  it("measures the tree with primary facets lifted to the top", () => {
    // Six lists and two number ranges, the shape of COMPANY_PRIMARY_FACETS.
    // The synthetic ranges have no presets, so they show min/max boxes only.
    // Their 40-option lists get the searchable dropdown, not checkboxes.
    const primary = [
      ...[0, 1, 2, 3, 4, 5].map((g) => `value.group_${g}.enum_0.value`),
      ...[6, 7].map((g) => `value.group_${g}.range_1.value`),
    ];
    measureCollapsed(
      "schema 12x10 = 120 leaves, 8 primary facets",
      makeSchema(12, 10),
      primary,
    );
    // "enum" names 48 of the 120 leaves; the search shows the first 20. The
    // tree stays mounted but hidden underneath, primary facets included.
    measureCollapsed(
      'schema 12x10 = 120 leaves, 8 primary facets, searching "enum"',
      makeSchema(12, 10),
      primary,
      "enum",
    );
  });

  it("measures buildFilterDoc() cost per filter interaction", () => {
    for (const [groups, leaves] of [
      [6, 5],
      [12, 10],
      [20, 15],
    ] as Array<[number, number]>) {
      const schema = makeSchema(groups, leaves);

      // Worst case: every list leaf carries a selection.
      const values: FilterValues = {};
      for (const group of schema.children ?? []) {
        for (const leaf of group.children ?? []) {
          if (leaf.allowed_values) {
            values[leaf.path] = {
              ...leaf,
              allowed_values: leaf.allowed_values.slice(0, 1),
            };
          }
        }
      }

      const ITER = 20;
      const t0 = Date.now();
      for (let i = 0; i < ITER; i++) buildFilterDoc(schema, values);
      const perCall = (Date.now() - t0) / ITER;

      log(
        `buildFilterDoc() over ${groups * leaves} leaves, every list leaf active: ` +
          `${perCall.toFixed(2)}ms per call (runs on every filter interaction)`,
      );
    }
  });
});
